/* STATUS / AGENT NOTES (2026-10-06)
 * Agent START Codex: Native touch scrolling, mouse momentum and faster continuous print discovery. */
(() => {
  if (customElements.get('scooch-limited-designs')) return;
  customElements.define('scooch-limited-designs', class extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.lp-prints__marquee');
      this.half = this.querySelector('.lp-prints__half');
      if (!this.viewport || !this.half || this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = 60; // Pixels/second, independent of the number of designs.
      this.velocity = this.speed;
      this.visible = false;
      this.position = this.viewport.scrollLeft;
      this.lastWritten = this.position;
      this.width = this.half.getBoundingClientRect().width;
      this.querySelectorAll('[aria-hidden="true"] a').forEach(link => link.tabIndex = -1);
      // Touch scrolling and its momentum belong to the browser; never capture or cancel them.
      this.viewport.addEventListener('touchstart', event => {
        clearTimeout(this.coastTimer);
        this.touching = true;
        this.coasting = false;
        this.touchX = event.touches[0].clientX;
        this.touchMoved = false;
        this.focused = false;
        this.pointerFocusUntil = performance.now() + 1000;
        // An equivalent copy gives a reverse swipe room at the start of the loop.
        if (!this.motion.matches && this.width && this.viewport.scrollLeft < this.width / 4) {
          this.viewport.scrollLeft += this.width;
        }
        this.position = this.lastWritten = this.viewport.scrollLeft;
        this.schedule();
      }, { ...options, passive: true });
      this.viewport.addEventListener('touchmove', event => {
        if (event.touches.length && Math.abs(event.touches[0].clientX - this.touchX) > 6) this.touchMoved = true;
      }, { ...options, passive: true });
      const touchRelease = event => {
        if (event.touches.length) return;
        this.touching = false;
        this.blockClickUntil = this.touchMoved ? performance.now() + 350 : 0;
        if (this.touchMoved) {
          this.coasting = true;
          this.waitForCoast();
        } else {
          this.velocity = this.speed;
          this.schedule();
        }
      };
      this.viewport.addEventListener('touchend', touchRelease, { ...options, passive: true });
      this.viewport.addEventListener('touchcancel', touchRelease, { ...options, passive: true });
      this.viewport.addEventListener('scroll', () => {
        if (this.drag || Math.abs(this.viewport.scrollLeft - this.lastWritten) <= .5) return;
        this.position = this.lastWritten = this.viewport.scrollLeft;
        if (this.touching) this.touchMoved = true;
        else {
          this.coasting = true;
          this.schedule();
          this.waitForCoast();
        }
      }, { ...options, passive: true });
      this.viewport.addEventListener('scrollend', () => {
        if (this.coasting && !this.touching) this.finishCoast();
      }, options);
      this.viewport.addEventListener('pointerdown', event => {
        this.focused = false;
        this.pointerFocusUntil = performance.now() + 1000;
        if (event.pointerType === 'touch' || !event.isPrimary || event.button !== 0) return;
        clearTimeout(this.coastTimer);
        this.coasting = false;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, time: event.timeStamp, moved: false };
        this.velocity = 0;
        this.schedule();
      }, options);
      this.viewport.addEventListener('pointermove', event => {
        const drag = this.drag;
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.moved && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 6) { this.drag = null; this.velocity = this.speed; this.schedule(); return; }
        if (!drag.moved && Math.abs(dx) <= 6) return;
        if (!drag.moved) { drag.moved = true; this.viewport.setPointerCapture(event.pointerId); this.viewport.dataset.dragging = "true"; }
        event.preventDefault();
        const elapsed = Math.max(8, event.timeStamp - drag.time);
        const distance = drag.lastX - event.clientX;
        this.move(distance);
        this.velocity = Math.max(-1200, Math.min(1200, distance * 1000 / elapsed));
        drag.lastX = event.clientX;
        drag.time = event.timeStamp;
      }, options);
      const release = event => {
        if (event.type === 'lostpointercapture' && event.target !== this.viewport) return;
        if (!this.drag || this.drag.id !== event.pointerId) return;
        this.blockClickUntil = this.drag.moved ? performance.now() + 350 : 0;
        if (!this.drag.moved || event.type === 'pointercancel' || event.timeStamp - this.drag.time > 100) this.velocity = this.speed;
        this.drag = null;
        delete this.viewport.dataset.dragging;
        if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
        this.schedule();
      };
      // A release outside the row must also clear an active mouse grip.
      document.addEventListener('pointerup', release, options);
      document.addEventListener('pointercancel', release, options);
      this.viewport.addEventListener('lostpointercapture', release, options);
      this.viewport.addEventListener('click', event => {
        if (performance.now() < (this.blockClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      }, { ...options, capture: true });
      this.viewport.addEventListener('dragstart', event => event.preventDefault(), options);
      this.addEventListener('focusin', event => {
        this.focused = !!(event.target.closest('.lp-print-tile') && event.target.matches(':focus-visible') && performance.now() >= (this.pointerFocusUntil || 0));
        this.schedule();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.target.closest('.lp-print-tile')) {
          this.pointerFocusUntil = 0;
          this.focused = true;
          this.schedule();
        }
      }, options);
      this.addEventListener('focusout', event => {
        this.focused = !!(this.contains(event.relatedTarget) && event.relatedTarget.closest('.lp-print-tile') && event.relatedTarget.matches(':focus-visible'));
        this.schedule();
      }, options);
      this.motion.addEventListener('change', () => { this.velocity = this.speed; this.schedule(); }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.resize = new ResizeObserver(() => { this.width = this.half.getBoundingClientRect().width; });
      this.resize.observe(this.half);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0 });
      this.observer.observe(this);
    }
    waitForCoast() {
      clearTimeout(this.coastTimer);
      // Older Safari releases lack scrollend. Resume only after native scrolling becomes quiet.
      this.coastTimer = setTimeout(() => this.finishCoast(), 180);
    }
    finishCoast() {
      if (this.touching) return;
      clearTimeout(this.coastTimer);
      this.coasting = false;
      this.position = this.lastWritten = this.viewport.scrollLeft;
      this.velocity = 0;
      this.schedule();
    }
    move(distance) {
      // Preserve fractional movement even when scrollLeft rounds small frame steps.
      if (Math.abs(this.viewport.scrollLeft - this.lastWritten) > .5) this.position = this.viewport.scrollLeft;
      const next = this.position + distance;
      this.position = this.motion.matches || !this.width ? Math.max(0, next) : ((next % this.width) + this.width) % this.width;
      this.viewport.scrollLeft = this.position;
      this.lastWritten = this.viewport.scrollLeft;
    }
    canAnimate() {
      return this.isConnected && this.visible && !document.hidden && !this.motion.matches && !this.drag && !this.touching && !this.coasting && !this.focused;
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.last = null;
      if (this.canAnimate()) this.frame = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      this.frame = null;
      if (!this.canAnimate()) { this.last = null; return; }
      const seconds = this.last == null ? 0 : Math.min((time - this.last) / 1000, .05);
      this.last = time;
      // Signed momentum settles back to the same autoplay speed after either swipe direction.
      this.velocity = this.speed + (this.velocity - this.speed) * Math.exp(-seconds / .65);
      this.move(this.velocity * seconds);
      this.frame = requestAnimationFrame(next => this.tick(next));
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.coastTimer);
      this.drag = null;
      this.touching = this.coasting = false;
    }
  });
})();
/* Agent END Codex: Native touch coast and continuous discovery. */
